import { getLevelizeByLabel } from './analysisUtils';
import { numberFormat, unitToString } from './unitUtils';

export const parseResults = data => {
  const results = { labels: [], datasets: [] };
  const datasets = data.series.map((ser, index) => {
    const dataset = { label: ser.value, data: [] };

    ser.points.forEach(([paramValue, resultValue]) => {
      if (index === 0) {
        results.labels.push(numberFormat(paramValue, 3, false));
      }

      dataset.data.push(numberFormat(resultValue, 3, false));
    });

    return dataset;
  });

  results.datasets = datasets;

  return results;
};

const getHierarchicalLabel = (paramsByNodeId, nodeId, paramPath) => {
  if (!paramsByNodeId?.[nodeId] || !paramPath) return null;

  const pathParts = paramPath.split('.');
  const labels = [];
  let current = paramsByNodeId[nodeId][pathParts[0]];

  if (!current) return null;

  if (current.label) labels.push(current.label);

  for (let i = 1; i < pathParts.length; i++) {
    if (!current.params?.length) break;

    const nestedParam = current.params.find(p => p.name === pathParts[i]);
    if (!nestedParam) break;

    if (nestedParam.label) labels.push(nestedParam.label);
    current = nestedParam;
  }

  return labels.length > 0 ? labels.join(' > ') : null;
};

export const getParamLabels = (workbenches, paramsByNodeId) => {
  return Array.from(workbenches.values()).map(workbench => {
    const { nodeId, paramName } = workbench;
    return getHierarchicalLabel(paramsByNodeId, nodeId, paramName);
  });
};

export const getWorkbenchUnits = workbenches => {
  return Array.from(workbenches.values()).map(({ unit }) => unitToString(unit));
};

export const getLCAYaxisLabel = (plot, node) => {
  const { levelizeUnit, levelizeBy } = plot;
  const unitObj = JSON.parse(levelizeUnit);
  const levelizeLabel = getLevelizeByLabel(node, levelizeBy);

  return `kgCO₂e/${unitToString(unitObj)} ${levelizeLabel}`;
};

/**
 * Recursively flattens nested params with hierarchical labels (e.g., "Capital Cost > Base Cost")
 * Filters by category and mc_eligible
 */
export const flattenParamsByCategory = (params, category) => {
  const flatParams = [];

  const traverse = (paramsList, parentPath = '', parentLabels = [], parentCategory = null) => {
    paramsList.forEach(param => {
      const fullName = parentPath ? `${parentPath}.${param.name}` : param.name;
      const labelPath = param.label ? [...parentLabels, param.label] : parentLabels;
      const fullLabel = labelPath.filter(Boolean).join(' > ');
      const paramCategory = param?.category?.length ? param.category : parentCategory;
      const matchesCategory = Array.isArray(paramCategory)
        ? paramCategory.includes(category)
        : paramCategory === category;

      if (matchesCategory && param.mc_eligible) {
        flatParams.push({
          ...param,
          name: fullName,
          originalName: param.name,
          label: fullLabel || param.label || param.name,
          category: paramCategory,
        });
      }

      if (param.params?.length) {
        traverse(param.params, fullName, labelPath, paramCategory);
      }
    });
  };

  traverse(params);
  return flatParams;
};

/**
 * Finds a param in paramsByNodeId using dot notation path (e.g., "capital_cost.base_value")
 * @param {Object} paramsByNodeId - Object keyed by nodeId, containing param objects keyed by name
 * @param {string} nodeId - The node ID
 * @param {string} paramPath - Dot notation path to the param (e.g., "capital_cost.base_value")
 * @returns {Object|null} - The found param object or null
 */
export const findParamByPath = (paramsByNodeId, nodeId, paramPath) => {
  if (!paramsByNodeId?.[nodeId] || !paramPath) return null;

  const pathParts = paramPath.split('.');
  let current = paramsByNodeId[nodeId][pathParts[0]];
  if (!current) return null;

  if (pathParts.length === 1) return current;

  for (let i = 1; i < pathParts.length; i++) {
    if (!current.params?.length) return null;

    const nestedParam = current.params.find(p => p.name === pathParts[i]);
    if (!nestedParam) return null;

    current = nestedParam;
  }

  return current;
};

/**
 * Gets the category for a param, falling back to parent categories if the param doesn't have one
 * @param {Object} paramsByNodeId - Object keyed by nodeId, containing param objects keyed by name
 * @param {string} nodeId - The node ID
 * @param {string} paramPath - Dot notation path to the param (e.g., "capital_cost.base_value")
 * @returns {Array|null} - The category array or null
 */
export const getCategoryFromPath = (paramsByNodeId, nodeId, paramPath) => {
  if (!paramsByNodeId?.[nodeId] || !paramPath) return null;

  const pathParts = paramPath.split('.');
  const topLevelParam = paramsByNodeId[nodeId][pathParts[0]];
  if (!topLevelParam) return null;

  // Traverse path, tracking category (inheriting from parents if param doesn't have one)
  let current = topLevelParam;
  let category = topLevelParam?.category?.length ? topLevelParam.category : null;

  for (let i = 1; i < pathParts.length; i++) {
    if (!current.params?.length) return category;

    const nestedParam = current.params.find(p => p.name === pathParts[i]);
    if (!nestedParam) return category;

    category = nestedParam?.category?.length ? nestedParam.category : category;
    current = nestedParam;
  }

  return category;
};
